<script lang="ts">
/** One action that fired on a result, as the hover lists it. */
export interface GaiaResultAction {
  /** Stable key — the action result's uid. */
  key: string;
  /** Past-tense label: "Email sent", "Signature prepared". */
  label: string;
  /** What it did — recipient, request, suggestion count. */
  detail?: string;
  /** The action type's glyph; adapt from actionTypeIcon at the call site. */
  icon?: Component;
  /** Colours label and glyph — `alert` for one that failed. Default reads as text. */
  tone?: 'text' | 'muted' | 'warning' | 'alert';
}
</script>

<script setup lang="ts">
import { computed, type Component, type PropType } from 'vue';
import { useThemeVars } from 'naive-ui';
import GaiaStatusChip, { type GaiaChipTone } from '../base/GaiaStatusChip.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaTextTone } from '../themes/tokens';

const props = defineProps({
  /**
   * Which answer this is, and so what heads the body: `outcome` for a
   * classification, clause validation or uncommons check, `extraction` for an
   * extracted value.
   */
  variant: {
    type: String as PropType<'outcome' | 'extraction'>,
    required: true,
  },
  /** The answer in full — this is where a row's clipped value is readable. */
  value: {
    type: String,
    required: true,
  },
  /**
   * Pills the answer in this tone, as the grid's tag types do (Low success,
   * Medium warning, High alert) — adapt a severity bucket at the call site.
   * Left off, the answer reads as text: an extracted value, or an outcome
   * that came back empty.
   */
  tone: {
    type: String as PropType<GaiaChipTone>,
    default: undefined,
  },
  /** The model's reasoning, when it recorded any. */
  reasoning: {
    type: String,
    default: undefined,
  },
  /** The actions this result fired, in the order the grid's icons run. */
  actions: {
    type: Array as PropType<GaiaResultAction[]>,
    default: () => [],
  },
});

const heading = computed<string>(() =>
  props.variant === 'outcome' ? 'Outcome' : 'Extracted value'
);

const themeVars = useThemeVars();

// One line of `meta` text, so an action's glyph can sit centred on its label.
const glyphBox = computed<string>(
  () => `calc(${gaiaFontSize.meta} * ${themeVars.value.lineHeight})`
);
</script>

<template>
  <!-- The body of a run result's hover, shared by the runs grid's cells and the
       per-run rows: the answer under its heading, then the reasoning. Capped
       and scrollable, because a reasoning paragraph has no length limit — the
       bubble's own width cap is GaiaTooltip's. -->
  <n-flex vertical :size="9" class="gaia-run-result-detail">
    <n-flex vertical :size="6" align="flex-start">
      <gaia-text size="body" weight="semibold" tone="ink">{{ heading }}</gaia-text>
      <gaia-status-chip v-if="props.tone" wrap :tone="props.tone">
        {{ props.value }}
      </gaia-status-chip>
      <gaia-text v-else size="body" tone="text" class="wrap">{{ props.value }}</gaia-text>
    </n-flex>

    <n-flex v-if="props.reasoning" vertical :size="4">
      <gaia-text size="meta" weight="semibold" tone="ink">Reasoning</gaia-text>
      <gaia-text size="meta" tone="text" class="wrap">{{ props.reasoning }}</gaia-text>
    </n-flex>

    <!-- What the result set off. The grid hangs these off one icon each in the
         cell's corner; here there is room to name them — the label on its own
         line with the detail beneath it, as the grid's action popover draws it.
         Beside it rather than under it squeezed the label into wrapping. -->
    <n-flex v-if="props.actions.length > 0" vertical :size="7">
      <gaia-text size="meta" weight="semibold" tone="ink">Actions</gaia-text>
      <n-flex
        v-for="action in props.actions"
        :key="action.key"
        align="flex-start"
        :size="7"
        :wrap="false"
      >
        <span v-if="action.icon" class="glyph">
          <n-icon
            :component="action.icon"
            :size="13"
            :color="gaiaTextTone[action.tone ?? 'text']"
          />
        </span>
        <n-flex vertical :size="2" :style="{ minWidth: 0 }">
          <gaia-text size="meta" weight="medium" :tone="action.tone ?? 'text'">
            {{ action.label }}
          </gaia-text>
          <gaia-text v-if="action.detail" size="meta" tone="muted" class="wrap">
            {{ action.detail }}
          </gaia-text>
        </n-flex>
      </n-flex>
    </n-flex>
  </n-flex>
</template>

<style scoped>
/* The cap and the scroller: a reasoning paragraph would otherwise grow the
   bubble past the viewport. Height only — the width cap is the tooltip's. */
.gaia-run-result-detail {
  max-height: 320px;
  overflow-y: auto;
}

/* One label line tall with the glyph centred inside it. Baseline alignment
   hangs a 13px icon off the text's baseline, and centring the whole row drifts
   as soon as the detail wraps below the label. */
.glyph {
  flex: none;
  display: flex;
  align-items: center;
  height: v-bind(glyphBox);
}

/* Model text arrives with its own newlines, and an extracted value can be one
   long unbroken token (a path, an id). */
.wrap {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
